import { useAppSelector } from "@/hooks/reduxHooks";
import { PVPApplicationForm } from "@/pvp/components/PVPApplicationForm";
import { PVPAppFormFooter } from "@/pvp/components/PVPAppFormFooter";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { PVPSectionList } from "@/pvp/components/PVPSectionList";
import { PageHeader } from "@/components/PageHeader";
import { useApiPvpRetrieve } from "@/api/lctApIComponents";
import { useDimensions } from "@/hooks/useDimensions";
import { FeedbackSection } from "@/pvp/components/FeedbackSection";
import { FeedbackButton } from "@/pvp/components/FeedbackSection/FeedbackButton";
import { useFormInstances } from "@/hooks/useFormInstances";
import { useFeedback } from "@/hooks/useFeedback";

type ApplicationSearch = {
  userAppId: string;
  appId: string;
};

export const Route = createFileRoute("/_auth/pvp/$pvpId")({
  validateSearch: (search: Record<string, unknown>): ApplicationSearch => {
    return {
      userAppId: (search.userAppId as string) || "",
      appId: (search.appId as string) || "",
    };
  },
  component: PVPApply,
});

function PVPApply() {
  const { pvpId } = Route.useParams();
  const { userAppId, appId } = Route.useSearch();
  const token = useAppSelector((state) => state.user.authKey);
  const isStaff = useAppSelector((state) => state.user.isStaff);
  const { isSmallScreen } = useDimensions();
  const formInstances = useFormInstances();
  const { feedbackItemsLength } = useFeedback(userAppId);

  const { data } = useApiPvpRetrieve({
    headers: { Authorization: `Token ${token}` },
    pathParams: { id: pvpId },
  });

  const hasFeedback = feedbackItemsLength > 0;
  const showFeedbackSection = hasFeedback || isStaff;
  const [showFeedback, setShowFeedback] = useState(showFeedbackSection);
  const toggleFeedback = () => {
    setShowFeedback(!showFeedback);
  };

  useEffect(() => {
    if (hasFeedback) {
      setShowFeedback(true);
    }
  }, [hasFeedback]);

  const hideSectionList = showFeedback && isSmallScreen;

  const emailLink = () => {
    return (
      <span>
        Need help? Contact us:{" "}
        <a href="mailto:info@lowcarbontech.com" className="text-blue-600 hover:text-blue-800 hover:underline">
          info@lowcarbontech.com
        </a>
      </span>
    );
  };

  return (
    <div className="min grid h-screen grid-cols-[1fr] grid-rows-[auto_auto_1fr_auto] overflow-hidden bg-gray-100 md:grid-cols-[auto_1fr_auto] md:grid-rows-[auto_1fr_auto]">
      <div className="col-span-full">
        <PageHeader title={data?.title} subtitle={data?.description} additionalText={emailLink()} />
      </div>
      <div className="col-span-1 flex flex-wrap items-center gap-x-4 overflow-auto p-4 md:m-6 md:items-start">
        {hideSectionList ? null : <PVPSectionList appId={appId} pvpId={pvpId} userAppId={userAppId} />}
        {!showFeedback && isSmallScreen && showFeedbackSection ? (
          <FeedbackButton onClick={toggleFeedback} hasFeedback={hasFeedback} />
        ) : null}
      </div>
      <div className="no-scrollbar col-span-1 min-h-0 min-w-[300px] max-w-[800px] overflow-y-auto px-4 md:p-8 md:pl-2 md:pr-4">
        <PVPApplicationForm pvpId={pvpId} appId={appId} userAppId={userAppId} formInstances={formInstances} />
      </div>
      {showFeedbackSection && showFeedback ? (
        <>
          {isSmallScreen && <div className="fixed inset-0 z-40 bg-black/20" onClick={toggleFeedback} />}
          <div
            className={`${
              isSmallScreen ? "fixed bottom-4 right-4 top-20 z-50 w-[90%] max-w-[400px]" : "col-span-1 min-h-0 px-4 md:pb-8 md:pt-6"
            }`}
          >
            <FeedbackSection pvpId={pvpId} appId={appId} userAppId={userAppId} toggleFeedback={toggleFeedback} />
          </div>
        </>
      ) : showFeedbackSection && !showFeedback && !isSmallScreen ? (
        <div className="col-span-1 min-h-0 px-4 md:pb-8 md:pt-6">
          <FeedbackButton onClick={toggleFeedback} hasFeedback={hasFeedback} />
        </div>
      ) : null}

      <div className="col-span-full">
        <PVPAppFormFooter pvpId={pvpId} appId={appId} userAppId={userAppId} formInstances={formInstances} />
      </div>
    </div>
  );
}
